import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const REASONS: Record<string, { title: string; message: string; charged: boolean }> = {
  payment_processing: {
    title: 'Payment Processing Error',
    message:
      'There was a problem processing your payment. Your card has not been charged and no booking was created.',
    charged: false,
  },
  booking_creation: {
    title: 'Booking Could Not Be Created',
    message:
      'Your payment was processed but we encountered an error while creating your booking. Please contact our support team immediately with your payment confirmation so we can resolve this for you.',
    charged: true,
  },
  session_invalid: {
    title: 'Session Invalid or Expired',
    message:
      'The payment session is invalid or has expired. Your card has not been charged. Please try booking again.',
    charged: false,
  },
  canceled: {
    title: 'Payment Canceled',
    message:
      'Your payment was canceled. Your card has not been charged. You can try again at any time.',
    charged: false,
  },
  generic: {
    title: 'Something Went Wrong',
    message:
      'An unexpected error occurred during the booking process. Your card has not been charged and no booking reference was created. Please try again or contact support.',
    charged: false,
  },
};

const BookingError: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reasonKey = searchParams.get('reason') || 'generic';
  const eventUuid = searchParams.get('event');
  const customMessage = searchParams.get('msg');

  const reason = REASONS[reasonKey] ?? REASONS.generic;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-6">

        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{reason.title}</h1>
        </div>

        {/* Charge status banner */}
        {reason.charged ? (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-yellow-600 text-xl leading-none mt-0.5">⚠️</span>
              <div>
                <p className="font-semibold text-yellow-800">Action Required</p>
                <p className="text-yellow-700 text-sm mt-1">
                  A payment may have been taken. Please contact us immediately so we can
                  issue your tickets or arrange a refund.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="text-green-600 text-xl leading-none mt-0.5">✅</span>
              <div>
                <p className="font-semibold text-green-800">You Have Not Been Charged</p>
                <p className="text-green-700 text-sm mt-1">
                  No payment has been taken and no booking reference was created.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error message card */}
        <div className="bg-white rounded-xl shadow-md p-6 space-y-3">
          <p className="text-gray-700 leading-relaxed">
            {customMessage || reason.message}
          </p>

          {reasonKey === 'booking_creation' && (
            <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-1">When contacting support, please provide:</p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Your full name and email address</li>
                <li>The event you were booking for</li>
                <li>The Stripe payment confirmation or receipt email</li>
              </ul>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          {eventUuid && (
            <button
              onClick={() => navigate(`/book-tickets/${eventUuid}`)}
              className="w-full py-3 px-6 bg-gradient-to-r from-primary-600 to-warm-700 hover:from-primary-700 hover:to-warm-800 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md"
            >
              Try Booking Again
            </button>
          )}

          <a
            href="mailto:support@soundx724.com"
            className="block w-full py-3 px-6 text-center bg-white border-2 border-primary-300 hover:border-primary-500 text-primary-700 hover:text-primary-900 font-semibold rounded-lg transition-all"
          >
            Contact Support
          </a>

          <button
            onClick={() => navigate('/events')}
            className="w-full py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-all"
          >
            View All Events
          </button>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400">
          If you believe you were charged in error, email us at{' '}
          <a href="mailto:support@soundx724.com" className="underline text-primary-500">
            support@soundx724.com
          </a>{' '}
          and we will respond within 24 hours.
        </p>

      </div>
    </div>
  );
};

export default BookingError;
